import React from 'react';

import useAddOns from 'Hooks/useAddOns';
import useAddOnsContext from 'Hooks/useAddOnsContext';
import addOnStore from 'Models/AddOnStore';
import { AdditionalBookingSectionEntity } from 'Models/Entities';
import { getFerryAndRouteIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import { compareFnByNumberAsc } from 'Util/_HumanWritten/SortUtils';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { AddOnSection } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/AddOn/AddOnSection';
import If from 'Views/Components/If/If';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import useAsync from 'Hooks/useAsync';
import alertToast from 'Util/ToastifyUtils';
import { AddOnsTabData, getAddOnCapacityRestrictions } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { WLIDS } from 'Constants';
import { canAdjustRestrictedAddOns } from 'Util/_HumanWritten/addOnPermissions';

export interface AddOnsCollectionProps {
	bookingType: TabSelected;
	userId?: string;
	showAddOnsHeader?: boolean;
}

export function AddOnsCollection({ bookingType, userId, showAddOnsHeader = false }: AddOnsCollectionProps) {
	const { selectedTrips } = useAddOnsContext();
	const { wizardData, setWizardData } = useBookingWizardDataStore();

	useAddOns(userId, true);

	const response = useAsync(async () => {
		try {
			if (isNullOrUndefined(wizardData.departingJourney)) {
				return {
					departureTickets: [],
					returningTickets: [],
				} as AddOnsTabData;
			}
			const res = await getAddOnCapacityRestrictions(
				wizardData.departingJourney,
				wizardData.returningJourney ?? null,
				wizardData.bookingToEdit ?? null,
				userId,
			);

			const newData = new BookingWizardData({ ...wizardData });

			res?.departureTickets?.forEach(restriction => {
				const matchingAddOn = newData.innerWizardData.departingTripOptions.find(
					x => x.optionId === restriction.addOnId,
				);
				if (isNotNullOrUndefined(matchingAddOn) && matchingAddOn.amount > restriction.remainingCapacity) {
					matchingAddOn.amount = restriction.remainingCapacity;
					matchingAddOn.adjustedBySystem = true;
				}
			});
			res?.returningTickets?.forEach(restriction => {
				const matchingAddOn = newData.innerWizardData.returningTripOptions.find(
					x => x.optionId === restriction.addOnId,
				);
				if (isNotNullOrUndefined(matchingAddOn) && matchingAddOn.amount > restriction.remainingCapacity) {
					matchingAddOn.amount = restriction.remainingCapacity;
					matchingAddOn.adjustedBySystem = true;
				}
			});

			setWizardData(newData);

			return res;
		} catch (err) {
			alertToast('Could not find add-on capacities', 'error', undefined);
			throw err;
		}
	}, []);

	if (response.type === 'error') {
		alertToast('Could not find add-on capacities', 'error');
	}
	const allSectionsRaw = useFilterAddOnSections(
		addOnStore.getAddOnSectionsByFerriesAndRoutes(getFerryAndRouteIds(selectedTrips, 'all')),
		bookingType,
	);

	return (
		<div>
			<If
				condition={
					showAddOnsHeader &&
					((selectedTrips.departingTrip && allSectionsRaw.length > 0) ||
						(selectedTrips.returningTrip && allSectionsRaw.length > 0))
				}
			>
				<h4 className="booking-wizard__tab-header mb-xs">
					<FormattedMessage id={WLIDS.addOns.title} defaultMessage="Add ons" />
				</h4>
			</If>
			{selectedTrips.departingTrip && allSectionsRaw.length > 0 && (
				<div className="booking-section-stack">
					{allSectionsRaw.map(x => (
						<AddOnSection
							key={x.id}
							addOnSection={x}
							direction="DEPARTURE"
							canBeAppliedToReturnTrip={allSectionsRaw?.some(s => s.id === x.id)}
							capacityRestrictions={response.data}
							bookingType={wizardData.tabSelected}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function useFilterAddOnSections(addOnSections: AdditionalBookingSectionEntity[], bookingType: TabSelected) {
	const { oldWizardData } = useBookingWizardDataStore();

	const isAlteration = oldWizardData?.wizardMode === 'ALTERATION';

	// Collect existing add-on IDs from the original booking for alteration mode
	const existingAddOnIds = new Set<string>();
	if (isAlteration) {
		for (const opt of oldWizardData?.innerWizardData?.departingTripOptions ?? []) {
			if (opt.amount > 0) {
				existingAddOnIds.add(opt.optionId);
			}
		}
		for (const opt of oldWizardData?.innerWizardData?.returningTripOptions ?? []) {
			if (opt.amount > 0) {
				existingAddOnIds.add(opt.optionId);
			}
		}
	}

	return addOnSections
		.filter(section => {
			// For new bookings, skip entirely disabled sections
			if (!isAlteration && section.disabled) {
				return false;
			}

			// For alterations, skip disabled sections unless user has options from it
			if (isAlteration && section.disabled) {
				const hasExisting = section.additionalBookingOptions.some(opt => existingAddOnIds.has(opt.id));
				if (!hasExisting) {
					return false;
				}
			}

			const addOns = section.additionalBookingOptions.filter(option => {
				// Restricted add-ons are hidden unless the caller may adjust them.
				if (option.isRestricted && !canAdjustRestrictedAddOns()) {
					if (!isAlteration) {
						return false;
					}
					return existingAddOnIds.has(option.id);
				}

				if (isAlteration) {
					// Exclude add-ons that can only be added during booking creation
					if (option.isInitialBookingOnly) {
						return false;
					}
					// For disabled options, only show if user already has them
					if (option.disabled || section.disabled) {
						return existingAddOnIds.has(option.id);
					}
				} else if (option.disabled) {
					// For new bookings, hide disabled options
					return false;
				}
				return true;
			});

			if (bookingType === 'vehicle') {
				// We want sections that have vehicle add-ons
				const vehicleAddOn = addOns.find(addOn => addOn.vehicleAddOn);
				return vehicleAddOn !== undefined;
			}

			if (bookingType === 'freight') {
				const freightAddOn = addOns.find(addOn => addOn.freightAddOn);
				return freightAddOn !== undefined;
			}

			// We want sections that have passenger add-ons
			const passengerAddOn = addOns.find(addOn => addOn.passengerAddOn);
			return passengerAddOn !== undefined;
		})

		.sort(compareFnByNumberAsc(x => x?.order));
}

export default observer(AddOnsCollection);
